import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  List,
  ListItem,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MdLocationOn } from 'react-icons/md';
import {
  autocompletePlaces,
  createPlacesSessionToken,
  getPlaceDetails,
} from '../../services/googlePlaces';
import { normalizeVillageName } from '../../data/master/villages';

function matchVillageFromPlace(villages, place) {
  if (!Array.isArray(villages) || !villages.length || !place) return null;
  const pin = String(place.pin || '').trim();
  if (pin) {
    const byPin = villages.find((v) => String(v.pin || '').trim() === pin);
    if (byPin) return byPin;
  }

  const candidates = [place.village, place.taluka, place.district]
    .map((v) => normalizeVillageName(v))
    .filter(Boolean);

  for (const needle of candidates) {
    const exact = villages.find(
      (v) => normalizeVillageName(v.villageName) === needle,
    );
    if (exact) return exact;
  }
  for (const needle of candidates) {
    const partial = villages.find((v) => {
      const name = normalizeVillageName(v.villageName);
      return name.includes(needle) || needle.includes(name);
    });
    if (partial) return partial;
  }
  return null;
}

/**
 * Google Places search via backend proxy — fills address lines and optionally matches Village Master.
 */
export function GooglePlacesSearch({
  label = 'Search location (Google)',
  villages = [],
  onResolved,
  helperText = 'Select a place to fill address details.',
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');
  const sessionRef = useRef(null);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const ensureSession = () => {
    if (!sessionRef.current) {
      sessionRef.current = createPlacesSessionToken();
    }
    return sessionRef.current;
  };

  const runSearch = async (value) => {
    const q = value.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const results = await autocompletePlaces(q, {
        sessionToken: ensureSession(),
      });
      setSuggestions(results);
      setOpen(true);
    } catch (err) {
      setSuggestions([]);
      setError(err?.message || 'Places search failed');
    } finally {
      setLoading(false);
    }
  };

  const onChangeQuery = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 300);
  };

  const onPick = async (suggestion) => {
    setOpen(false);
    setSuggestions([]);
    setQuery(suggestion.description || suggestion.mainText || '');
    setResolving(true);
    setError('');
    try {
      const place = await getPlaceDetails(suggestion.placeId, {
        sessionToken: sessionRef.current,
      });
      sessionRef.current = null;
      const matchedVillage = matchVillageFromPlace(villages, place);
      onResolved?.({
        ...place,
        matchedVillage,
        villageId: matchedVillage?.villageId || '',
      });
    } catch (err) {
      setError(err?.message || 'Could not load place details');
    } finally {
      setResolving(false);
    }
  };

  const statusText = useMemo(() => {
    if (error) return error;
    return helperText;
  }, [error, helperText]);

  return (
    <FormControl ref={wrapRef}>
      <FormLabel>{label}</FormLabel>
      <Box position="relative">
        <Input
          value={query}
          onChange={onChangeQuery}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder="e.g. Mundra, Kutch or full street address"
          size="lg"
          fontSize="15px"
          fontWeight="500"
          h="52px"
          bg="bg.input"
          isDisabled={resolving}
          pr={10}
        />
        {(loading || resolving) && (
          <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
            <Spinner size="sm" color="utkarsh.amber" />
          </Box>
        )}
        {open && suggestions.length > 0 ? (
          <List
            position="absolute"
            zIndex={20}
            mt={1}
            w="100%"
            maxH="240px"
            overflowY="auto"
            bg="bg.surface"
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            boxShadow="md"
          >
            {suggestions.map((s) => (
              <ListItem
                key={s.placeId}
                px={3}
                py={2.5}
                cursor="pointer"
                _hover={{ bg: 'bg.input' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPick(s)}
              >
                <VStack align="start" spacing={0}>
                  <Text fontSize="14px" fontWeight="600" display="flex" gap={2}>
                    <Box as={MdLocationOn} mt="2px" color="utkarsh.amber" />
                    {s.mainText || s.description}
                  </Text>
                  {s.secondaryText ? (
                    <Text fontSize="12px" color="text.secondary" pl={6}>
                      {s.secondaryText}
                    </Text>
                  ) : null}
                </VStack>
              </ListItem>
            ))}
          </List>
        ) : null}
      </Box>
      <FormHelperText color={error ? 'red.500' : 'text.secondary'}>
        {statusText}
      </FormHelperText>
    </FormControl>
  );
}
